import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlanner } from '../hooks/usePlanner';
import { supabase } from '../lib/supabase';
import { C } from '../lib/theme';
import { getProteinTarget } from '../lib/nutrition';

const MEAL_ORDER = ['desayuno', 'comida', 'cena'];

function formatDate(dateStr, dayNames, monthNames) {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  return `${dayNames[dow === 0 ? 6 : dow - 1]} ${d.getDate()} ${monthNames[d.getMonth()]}`;
}

function getDayMacros(day) {
  let calories = 250, protein = 0;
  for (const meal of Object.values(day.meals || {})) {
    const items = Array.isArray(meal) ? meal : [meal];
    for (const r of items) {
      calories += r.calories_per_serving || 0;
      protein  += r.protein_per_serving  || 0;
    }
  }
  return { calories: Math.round(calories), protein: Math.round(protein) };
}

function scaleMainIngredient(key, value, mainFoods, scaleFactor = 1.5) {
  const keyLower = key.toLowerCase();
  const isMain = (mainFoods || []).some(mf => {
    const mfLower = mf.toLowerCase();
    return keyLower.includes(mfLower) ||
      mfLower.split(' ').some(w => w.length > 3 && keyLower.includes(w));
  });
  if (!isMain) return value;
  const match = value.match(/^(\d+(?:[.,]\d+)?)(.*)/);
  if (!match) return value;
  const scaled = Math.round(parseFloat(match[1].replace(',', '.')) * scaleFactor);
  return `${scaled}${match[2]}`;
}

// ─── DISLIKES SETUP SCREEN ──────────────────────────────────────────────────

function DislikesScreen({ foods, currentDislikes, currentCookingStyle, onSave, saving }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(new Set(currentDislikes));
  const [cookingStyle, setCookingStyle] = useState(currentCookingStyle || 'tradicional');

  const CATEGORY_LABELS = {
    carnes:    t('planner.cat_meats'),
    pescados:  t('planner.cat_fish'),
    lácteos:   t('planner.cat_dairy'),
    verduras:  t('planner.cat_vegetables'),
    legumbres: t('planner.cat_legumes'),
    cereales:  t('planner.cat_cereals'),
    frutas:    t('planner.cat_fruits'),
    grasas:    t('planner.cat_fats'),
  };

  const byCategory = foods.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.4rem', color: C.carbon, fontFamily: 'Georgia, serif' }}>
          {t('planner.dislikes_title')}
        </h3>
        <p style={{ margin: '0 0 1.2rem', color: C.gray, fontSize: '0.85rem' }}>
          {t('planner.dislikes_sub')}
        </p>

        {Object.entries(byCategory).map(([cat, items]) => (
          <div key={cat} style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.sage, marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {CATEGORY_LABELS[cat] || cat}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {items.map(food => {
                const active = selected.has(food.id);
                return (
                  <button
                    key={food.id}
                    onClick={() => toggle(food.id)}
                    style={{
                      padding: '0.3rem 0.65rem', borderRadius: 20, fontSize: '0.8rem', cursor: 'pointer',
                      border: `1.5px solid ${active ? C.terra : C.mint}`,
                      background: active ? '#FEE2E2' : '#fff',
                      color: active ? C.terra : C.carbon,
                      transition: 'all 0.15s',
                    }}
                  >
                    {food.emoji} {food.name}{active ? ' ✕' : ''}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {selected.size > 0 && (
        <p style={{ textAlign: 'center', color: C.terra, fontSize: '0.82rem', margin: '0 0 0.8rem' }}>
          {t('planner.excluded_count', { count: selected.size })}
        </p>
      )}

      <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.8rem', color: C.carbon, fontFamily: 'Georgia, serif' }}>{t('planner.cooking_title')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          {[
            { value: 'tradicional', emoji: '🫕', labelKey: 'traditional_label', descKey: 'traditional_desc' },
            { value: 'moderno',     emoji: '🌟', labelKey: 'modern_label',       descKey: 'modern_desc' },
          ].map(opt => (
            <div
              key={opt.value}
              onClick={() => setCookingStyle(opt.value)}
              style={{
                padding: '0.8rem', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                background: cookingStyle === opt.value ? C.mintLt : C.cream,
                border: `2px solid ${cookingStyle === opt.value ? C.sage : C.mint}`,
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: '1.4rem' }}>{opt.emoji}</div>
              <div style={{ fontWeight: 700, color: C.carbon, fontSize: '0.85rem' }}>{t(`planner.${opt.labelKey}`)}</div>
              <div style={{ fontSize: '0.7rem', color: C.gray, marginTop: '0.15rem' }}>{t(`planner.${opt.descKey}`)}</div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={() => onSave(Array.from(selected), cookingStyle)}
        disabled={saving}
        style={{
          width: '100%', padding: '1rem', borderRadius: 12, border: 'none',
          background: C.sage, color: '#fff', fontSize: '1rem', fontWeight: 700,
          cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1,
        }}
      >
        {saving ? t('planner.generating') : t('planner.generate_btn')}
      </button>
    </div>
  );
}

// ─── RECIPE CARD ────────────────────────────────────────────────────────────

function RecipeCard({ recipe, mealType, slotLabel, onShuffle, scaleFactor = 1.5 }) {
  const { t } = useTranslation();
  const [showDetail, setShowDetail] = useState(false);

  const MEAL_META = {
    desayuno: { label: t('planner.meal_breakfast'), icon: '☀️' },
    comida:   { label: t('planner.meal_lunch'),     icon: '🍽️' },
    cena:     { label: t('planner.meal_dinner'),    icon: '🌙' },
    postre:   { label: t('planner.meal_dessert'),   icon: '🍮' },
    guarrada: { label: t('planner.meal_treat'),     icon: '⚡' },
  };

  const meta = MEAL_META[mealType] || {};

  return (
    <div style={{ background: C.cream, borderRadius: 10, padding: '0.7rem', marginBottom: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        <div style={{ flex: 1 }}>
          <div
            style={{ fontSize: '0.85rem', fontWeight: 700, color: C.carbon, marginBottom: '0.2rem', cursor: 'pointer' }}
            onClick={() => setShowDetail(v => !v)}
          >
            {slotLabel ? `${meta.icon} ${slotLabel} — ` : `${meta.icon} `}{recipe.name}
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', color: C.terra, fontWeight: 700 }}>{recipe.calories_per_serving} kcal</span>
            <span style={{ fontSize: '0.72rem', color: C.sage,  fontWeight: 700 }}>{recipe.protein_per_serving}g prot</span>
            <span style={{ fontSize: '0.72rem', color: C.gray }}>⏱ {recipe.prep_time_minutes}min</span>
          </div>
        </div>
        <button
          onClick={onShuffle}
          title="Cambiar receta"
          style={{
            background: '#fff', border: `1.5px solid ${C.mint}`, borderRadius: 8,
            padding: '0.3rem 0.45rem', cursor: 'pointer', fontSize: '0.9rem',
            color: C.sage, flexShrink: 0,
          }}
        >
          🔀
        </button>
      </div>

      {showDetail && (
        <div style={{ marginTop: '0.6rem', borderTop: `1px solid ${C.mint}`, paddingTop: '0.6rem' }}>
          <div style={{ fontSize: '0.78rem', color: C.gray, marginBottom: '0.3rem', fontWeight: 600 }}>{t('planner.ingredients_title')}</div>
          {Object.entries(recipe.ingredients || {}).map(([k, v]) => (
            <div key={k} style={{ fontSize: '0.78rem', color: C.carbon }}>
              • {k}: {scaleMainIngredient(k, v, recipe.main_foods, scaleFactor)}
            </div>
          ))}
          <div style={{ fontSize: '0.78rem', color: C.gray, marginTop: '0.5rem', fontWeight: 600 }}>{t('planner.instructions_title')}</div>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: C.carbon, lineHeight: 1.5 }}>
            {recipe.instructions}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── PLAN SCREEN ────────────────────────────────────────────────────────────

function PlanScreen({ currentPlan, shuffleRecipe, toggleFasting, onRegenerate, onChangePrefs, proteinTarget }) {
  const { t } = useTranslation();
  const [expandedDay, setExpandedDay] = useState(0);

  const dayNames   = t('common.days_short_mon', { returnObjects: true });
  const monthNames = t('common.months_short',   { returnObjects: true });

  function guarradaMessage(recipe) {
    if (recipe.protein_per_serving >= 14) {
      return t('planner.treat_high_protein', { name: recipe.name, protein: recipe.protein_per_serving });
    }
    return t('planner.treat_adjusted', { name: recipe.name });
  }

  const weeklyMacros = currentPlan.days.reduce((acc, day) => {
    const { calories, protein } = getDayMacros(day);
    return { calories: acc.calories + calories, protein: acc.protein + protein };
  }, { calories: 0, protein: 0 });

  const avgCal  = Math.round(weeklyMacros.calories / 7);
  const avgProt = Math.round(weeklyMacros.protein / 7);
  const weeklyProtTarget = proteinTarget * 7;
  const protOk = weeklyMacros.protein >= weeklyProtTarget;

  return (
    <div>
      <div style={{
        background: C.mintLt, borderRadius: 12, padding: '0.75rem 1rem',
        marginBottom: '1rem', borderLeft: `4px solid ${C.sage}`,
        fontSize: '0.8rem', color: C.carbon, lineHeight: 1.5,
      }}>
        <strong>{t('planner.snack_banner_bold')}</strong> — {t('planner.snack_banner_text')}
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '1rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.gray, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('planner.weekly_summary')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.8rem' }}>
          {[
            { label: t('planner.avg_kcal'),        value: avgCal,          color: C.terra },
            { label: t('planner.avg_protein'),      value: `${avgProt}g`,   color: C.sage },
            { label: t('planner.protein_target'),   value: `${proteinTarget}g`, color: C.carbon },
          ].map(s => (
            <div key={s.label} style={{ background: C.cream, borderRadius: 10, padding: '0.6rem', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.65rem', color: C.gray, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{
          padding: '0.5rem 0.8rem', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600,
          background: protOk ? '#D1FAE5' : '#FEF9C3',
          color: protOk ? '#065F46' : '#78350F',
        }}>
          {protOk
            ? t('planner.protein_ok', { actual: weeklyMacros.protein, target: weeklyProtTarget })
            : t('planner.protein_low', { actual: weeklyMacros.protein, target: weeklyProtTarget })
          }
        </div>
      </div>

      {currentPlan.days.map((day, dayIndex) => {
        const { calories, protein } = getDayMacros(day);
        const isExpanded = expandedDay === dayIndex;
        const visibleMeals = MEAL_ORDER.filter(tp =>
          !day.fasting || day.fasting_window !== `skip_${tp}`
        );
        const mainScaleFactor = (day.dessert_day || day.guarrada_day) ? 1.0 : 1.5;

        return (
          <div key={day.date} style={{ background: '#fff', borderRadius: 16, marginBottom: '0.7rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', overflow: 'hidden' }}>
            <div
              onClick={() => setExpandedDay(isExpanded ? null : dayIndex)}
              style={{
                padding: '0.8rem 1rem', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', cursor: 'pointer',
                background: day.fasting ? '#FFFBEB' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: C.carbon, fontSize: '0.9rem' }}>{formatDate(day.date, dayNames, monthNames)}</span>
                {day.fasting && (
                  <span style={{ fontSize: '0.7rem', color: '#92400E', background: '#FDE68A', borderRadius: 10, padding: '0.1rem 0.4rem' }}>
                    {t('planner.fasting_tag')}
                  </span>
                )}
                {day.dessert_day && (
                  <span style={{ fontSize: '0.7rem', color: '#6B21A8', background: '#F3E8FF', borderRadius: 10, padding: '0.1rem 0.4rem' }}>
                    {t('planner.dessert_tag')}
                  </span>
                )}
                {day.guarrada_day && (
                  <span style={{ fontSize: '0.7rem', color: '#92400E', background: '#FEF3C7', borderRadius: 10, padding: '0.1rem 0.4rem' }}>
                    {t('planner.treat_tag')}
                  </span>
                )}
                {day.injection_window && (
                  <span style={{ fontSize: '0.7rem', color: '#1D4ED8', background: '#DBEAFE', borderRadius: 10, padding: '0.1rem 0.4rem' }}>
                    {day.injection_day_itself ? t('planner.injection_tag') : t('planner.injection_plus24_tag')}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '0.72rem', color: C.terra }}>{calories} kcal</span>
                <span style={{ fontSize: '0.72rem', color: C.sage }}>{protein}g</span>
                <span style={{ color: C.gray, fontSize: '0.75rem' }}>{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {isExpanded && (
              <div style={{ padding: '0 1rem 1rem' }}>
                {day.injection_window && (
                  <div style={{
                    background: '#EFF6FF', border: '1.5px solid #BFDBFE',
                    borderRadius: 10, padding: '0.6rem 0.8rem', marginBottom: '0.8rem',
                    fontSize: '0.78rem', color: '#1E40AF', lineHeight: 1.5,
                  }}>
                    {day.injection_day_itself
                      ? t('planner.injection_day_note')
                      : t('planner.injection_plus24_note')}
                  </div>
                )}
                <div style={{ marginBottom: '0.8rem' }}>
                  <div style={{ fontSize: '0.72rem', color: C.gray, marginBottom: '0.3rem' }}>{t('planner.fasting_label')}</div>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    {[
                      { value: 'skip_desayuno', label: t('planner.skip_breakfast') },
                      { value: 'skip_cena',     label: t('planner.skip_dinner') },
                    ].map(opt => {
                      const active = day.fasting && day.fasting_window === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => toggleFasting(dayIndex, opt.value)}
                          style={{
                            flex: 1, padding: '0.35rem', borderRadius: 8,
                            fontSize: '0.72rem', cursor: 'pointer',
                            border: `1.5px solid ${active ? C.sage : C.mint}`,
                            background: active ? C.mintLt : '#fff',
                            color: active ? C.sage : C.gray,
                            fontWeight: active ? 700 : 400,
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {visibleMeals.map(type => {
                  const slot = day.meals[type];
                  if (!slot) return null;
                  const recipeList = Array.isArray(slot) ? slot : [slot];
                  const showLabel = recipeList.length > 1;
                  return (
                    <div key={type}>
                      {recipeList.map((recipe, idx) => (
                        <RecipeCard
                          key={`${type}-${idx}`}
                          recipe={recipe}
                          mealType={type}
                          slotLabel={showLabel ? t('planner.dish_label', { n: idx + 1 }) : null}
                          onShuffle={() => shuffleRecipe(dayIndex, type, idx)}
                          scaleFactor={mainScaleFactor}
                        />
                      ))}
                    </div>
                  );
                })}

                {day.guarrada_day && day.meals.guarrada && (
                  <div>
                    <div style={{
                      background: '#FFFBEB', border: '1.5px solid #FCD34D',
                      borderRadius: 10, padding: '0.6rem 0.8rem', marginBottom: '0.5rem',
                      fontSize: '0.78rem', color: '#78350F', lineHeight: 1.5,
                    }}>
                      {guarradaMessage(day.meals.guarrada)}
                    </div>
                    <RecipeCard
                      recipe={day.meals.guarrada}
                      mealType="guarrada"
                      onShuffle={() => shuffleRecipe(dayIndex, 'guarrada', 0)}
                      scaleFactor={1.5}
                    />
                  </div>
                )}

                {day.dessert_day && day.meals.postre && (
                  <div>
                    <div style={{
                      background: '#FAF5FF', border: '1.5px solid #D8B4FE',
                      borderRadius: 10, padding: '0.6rem 0.8rem', marginBottom: '0.5rem',
                      fontSize: '0.78rem', color: '#6B21A8', lineHeight: 1.5,
                    }}>
                      <strong>{t('planner.why_dessert_title')}</strong> {t('planner.why_dessert_text')}
                    </div>
                    <RecipeCard
                      recipe={day.meals.postre}
                      mealType="postre"
                      onShuffle={() => shuffleRecipe(dayIndex, 'postre', 0)}
                      scaleFactor={1.5}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', marginBottom: '1rem' }}>
        <button
          onClick={onRegenerate}
          style={{ flex: 1, padding: '0.8rem', borderRadius: 12, border: `1.5px solid ${C.mint}`, background: '#fff', color: C.sage, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}
        >
          {t('planner.regenerate_btn')}
        </button>
        <button
          onClick={onChangePrefs}
          style={{ flex: 1, padding: '0.8rem', borderRadius: 12, border: `1.5px solid ${C.mint}`, background: '#fff', color: C.sage, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}
        >
          {t('planner.change_prefs_btn')}
        </button>
      </div>
    </div>
  );
}

// ─── SHOPPING PROMPT ────────────────────────────────────────────────────────

function ShoppingPrompt({ onAccept, onDismiss }) {
  const { t } = useTranslation();
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: '#fff', borderRadius: '20px 20px 0 0',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.14)',
      padding: '1.2rem 1.2rem 2.2rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '1rem', fontWeight: 700, color: C.carbon, marginBottom: '0.3rem' }}>
            {t('planner.shopping_prompt_title')}
          </div>
          <div style={{ fontSize: '0.82rem', color: C.gray, lineHeight: 1.5 }}>
            {t('planner.shopping_prompt_sub')}
          </div>
        </div>
        <button
          onClick={onDismiss}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.gray, fontSize: '1.3rem', padding: '0 0 0 0.8rem', lineHeight: 1 }}
        >
          {t('common.close')}
        </button>
      </div>
      <button
        onClick={onAccept}
        style={{ width: '100%', padding: '0.85rem', borderRadius: 12, border: 'none', background: C.sage, color: '#fff', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' }}
      >
        {t('planner.shopping_yes')}
      </button>
    </div>
  );
}

// ─── SHOPPING LIST MODAL ─────────────────────────────────────────────────────

function ShoppingListModal({ grouped, onClose }) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(new Set());

  const SHOP_SECTIONS = [
    { key: 'carnes',    label: t('planner.shop_meats'),      icon: '🥩' },
    { key: 'pescados',  label: t('planner.shop_fish'),       icon: '🐟' },
    { key: 'verduras',  label: t('planner.shop_vegetables'), icon: '🥦' },
    { key: 'frutas',    label: t('planner.shop_fruits'),     icon: '🍎' },
    { key: 'lácteos',   label: t('planner.shop_dairy'),      icon: '🥛' },
    { key: 'cereales',  label: t('planner.shop_cereals'),    icon: '🍞' },
    { key: 'legumbres', label: t('planner.shop_legumes'),    icon: '🫘' },
    { key: 'grasas',    label: t('planner.shop_fats'),       icon: '🫒' },
    { key: 'otros',     label: t('planner.shop_other'),      icon: '🛒' },
  ];

  function toggle(key) {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  const totalItems   = Object.values(grouped).reduce((s, arr) => s + arr.length, 0);
  const checkedCount = checked.size;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: C.cream, display: 'flex', flexDirection: 'column' }}>
      <div style={{
        background: '#fff', padding: '1rem', borderBottom: `1px solid ${C.mint}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '1rem', fontWeight: 700, color: C.carbon }}>{t('planner.shopping_list_title')}</div>
          <div style={{ fontSize: '0.72rem', color: C.gray }}>{t('planner.shopping_checked', { done: checkedCount, total: totalItems })}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.gray, fontSize: '1.3rem' }}>{t('common.close')}</button>
      </div>

      <div style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
        {SHOP_SECTIONS.map(section => {
          const items = grouped[section.key];
          if (!items?.length) return null;
          return (
            <div key={section.key} style={{ marginBottom: '1.2rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.sage, marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {section.icon} {section.label}
              </div>
              <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 6px rgba(44,106,79,0.06)' }}>
                {items.map((item, idx) => {
                  const key = `${section.key}|${item.name}`;
                  const done = checked.has(key);
                  return (
                    <div
                      key={key}
                      onClick={() => toggle(key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.65rem',
                        padding: '0.7rem 0.85rem', cursor: 'pointer', userSelect: 'none',
                        borderBottom: idx < items.length - 1 ? `1px solid ${C.mintLt}` : 'none',
                      }}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                        border: `2px solid ${done ? C.sage : C.mint}`,
                        background: done ? C.sage : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {done && <span style={{ color: '#fff', fontSize: '0.65rem', fontWeight: 900 }}>✓</span>}
                      </div>
                      <span style={{ flex: 1, fontSize: '0.85rem', color: done ? C.gray : C.carbon, textDecoration: done ? 'line-through' : 'none' }}>
                        {item.name}
                      </span>
                      {item.display && (
                        <span style={{ fontSize: '0.78rem', color: C.gray, fontWeight: 600 }}>{item.display}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────

export default function PlannerTab({ userId, profile, onProfileUpdate }) {
  const { t } = useTranslation();
  const {
    foods, dislikes, dislikesLoaded, currentPlan, loading,
    saveDislikes, generatePlan, savePlan, shuffleRecipe, toggleFasting, buildShoppingList,
  } = usePlanner(userId, profile);

  const [screen, setScreen] = useState('loading');
  const [saving, setSaving] = useState(false);
  const [showShoppingPrompt, setShowShoppingPrompt] = useState(false);
  const [shoppingList, setShoppingList] = useState(null);

  const proteinTarget = getProteinTarget(profile);

  useEffect(() => {
    if (loading || !dislikesLoaded) return;
    if (currentPlan) {
      setScreen('plan');
    } else {
      setScreen('setup');
    }
  }, [loading, dislikesLoaded, currentPlan]);

  async function handleSaveDislikes(ids, cookingStyle) {
    setSaving(true);
    await saveDislikes(ids);
    if (cookingStyle && cookingStyle !== profile?.cooking_style) {
      await supabase.from('profiles').update({ cooking_style: cookingStyle }).eq('id', userId);
      onProfileUpdate?.();
    }
    const plan = generatePlan(ids);
    await savePlan(plan);
    setSaving(false);
    setScreen('plan');
    setShoppingList(null);
    setShowShoppingPrompt(true);
  }

  async function handleRegenerate() {
    setSaving(true);
    const plan = generatePlan();
    await savePlan(plan);
    setSaving(false);
    setShoppingList(null);
    setShowShoppingPrompt(true);
  }

  function handleAcceptShopping() {
    setShowShoppingPrompt(false);
    setShoppingList(buildShoppingList(currentPlan));
  }

  if (screen === 'loading') {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: C.gray, fontSize: '0.9rem' }}>
        {t('planner.loading')}
      </div>
    );
  }

  if (screen === 'setup') {
    return (
      <DislikesScreen
        foods={foods}
        currentDislikes={dislikes}
        currentCookingStyle={profile?.cooking_style}
        onSave={handleSaveDislikes}
        saving={saving}
      />
    );
  }

  return (
    <>
      <PlanScreen
        currentPlan={currentPlan}
        shuffleRecipe={shuffleRecipe}
        toggleFasting={toggleFasting}
        onRegenerate={handleRegenerate}
        onChangePrefs={() => setScreen('setup')}
        proteinTarget={proteinTarget}
      />
      {showShoppingPrompt && (
        <ShoppingPrompt
          onAccept={handleAcceptShopping}
          onDismiss={() => setShowShoppingPrompt(false)}
        />
      )}
      {shoppingList && (
        <ShoppingListModal
          grouped={shoppingList}
          onClose={() => setShoppingList(null)}
        />
      )}
    </>
  );
}
